/**
 * One row of the Part Repository table.
 *
 * Split out of PartRepositoryTab (CR-7717) to keep that component under the
 * 500-line limit. Presentational: the selection and the supplier actions
 * arrive as props. CR-8613: the Direct tick moved to Stock Planning, the one
 * place it is set.
 *
 * @link PartRepositoryTab for the consumer.
 */
import type { PartCatalogItem } from '../../../lib/api/types';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { TableCell, TableRow } from '../../ui/table';
import { Users, X } from 'lucide-react';

interface PartRepositoryRowProps {
  part: PartCatalogItem;
  selected: boolean;
  onToggleSelect: (id: number) => void;
  onDoubleClick: (part: PartCatalogItem) => void;
  onRemoveSuppliers: (id: number) => void;
}

export default function PartRepositoryRow({
  part, selected, onToggleSelect, onDoubleClick, onRemoveSuppliers,
}: PartRepositoryRowProps) {
  return (
    <TableRow className="cursor-pointer" onDoubleClick={() => onDoubleClick(part)}>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(part.id)} />
      </TableCell>
      <TableCell className="font-medium">{part.part_number}</TableCell>
      <TableCell>{part.description}</TableCell>
      <TableCell className="text-muted-foreground">{part.alt_part_number || '-'}</TableCell>
      <TableCell className="text-right">{part.pallet_qty}</TableCell>
      <TableCell className="text-right">{part.load_qty}</TableCell>
      <TableCell>
        {part.suppliers.length === 0 ? (
          <span className="text-muted-foreground text-sm">Unassigned</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {part.suppliers.map((s) => (
              <Badge key={s.id} variant="secondary" className="text-xs">
                <Users className="h-3 w-3 mr-1" />
                {s.name}
              </Badge>
            ))}
          </div>
        )}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        {part.suppliers.length > 0 && (
          <Button
            variant="ghost" size="icon" className="h-7 w-7"
            onClick={() => onRemoveSuppliers(part.id)}
            title="Remove all suppliers"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
